/**
 * A qual bloco do formulário uma linha de mensalidade pertence.
 *
 * Os blocos 4A (parcial) e 4B (integral) ofereciam a lista inteira de
 * mensalidades, misturando os dois turnos: quem preenchia o integral via a
 * "Parcial 1º ano" na frente e podia escolher errado sem nada reclamar.
 *
 * A classificação tem duas fontes, nesta ordem:
 *
 * 1. **O campo `periodoValor`**, escolhido pelo gestor no cadastro. Manda.
 * 2. **A descrição**, quando o campo está vazio. É o que faz o filtro funcionar
 *    já no primeiro deploy, com as linhas que existem hoje ("Parcial 2º ao 5º
 *    ano com Bilíngue", "Integral 1º ano…"), sem ninguém recadastrar nada.
 *
 * Não achando nem um nem outro, a linha aparece **nos dois** blocos. Esconder
 * um valor por falta de classificação seria pior que mostrar de mais: o
 * consultor ficaria sem a opção que precisa, no meio do atendimento, sem
 * entender por quê.
 */
import type { PeriodoValor, TabelaValor } from "./tipos";

/** Ignora acento e caixa: "INTEGRAL", "Integral" e "íntegral" contam igual. */
const normalizar = (t: string) =>
  t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function periodoDaLinha(v: Pick<TabelaValor, "descricao" | "periodoValor">): PeriodoValor | null {
  if (v.periodoValor) return v.periodoValor;
  const d = normalizar(v.descricao ?? "");
  // a ordem importa: "Integral" é a palavra mais específica das duas, e uma
  // descrição que cite as duas ("parcial e integral") não deve ser classificada
  const temIntegral = /\bintegral\b/.test(d);
  const temParcial = /\bparcial\b/.test(d);
  if (temIntegral && !temParcial) return "INTEGRAL";
  if (temParcial && !temIntegral) return "PARCIAL";
  return null;
}

/** A linha serve a este bloco? Sem classificação, serve aos dois. */
export function serveAoBloco(
  v: Pick<TabelaValor, "descricao" | "periodoValor">,
  bloco: PeriodoValor,
): boolean {
  const p = periodoDaLinha(v);
  return p === null || p === bloco;
}

export const ROTULO_PERIODO_VALOR: Record<PeriodoValor, string> = {
  PARCIAL: "Parcial",
  INTEGRAL: "Integral",
};
